import { formatDate } from '@/shared/lib';
import { Temporal } from '@shared/lib/date-time';

export type DateRange = {
  dateFrom: string;
  dateTo: string;
};

const isIsoDate = (value: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(value);

export const getDateRangeError = (dateFrom: string, dateTo: string): string | null => {
  if ((dateFrom && !isIsoDate(dateFrom)) || (dateTo && !isIsoDate(dateTo))) {
    return 'Введите дату как 09.11.2026.';
  }

  if (dateFrom && dateTo && dateFrom > dateTo) {
    return 'Дата начала периода не может быть позже даты окончания.';
  }

  return null;
};

export const formatDateRange = (dateFrom: string, dateTo: string): string => {
  if (dateFrom && dateTo) {
    return `с ${formatDate(dateFrom)} по ${formatDate(dateTo)}`;
  }
  if (dateFrom) {
    return `с ${formatDate(dateFrom)}`;
  }
  if (dateTo) {
    return `по ${formatDate(dateTo)}`;
  }

  return '';
};

/** Разбирает дату как её напечатали: `9.11.26`, `09/11/2026`, `09112026`. */
export const parseTypedDate = (text: string): Temporal.PlainDate | null => {
  const value = text.trim();
  const parts = /^\d{8}$/.test(value) ? [value.slice(0, 2), value.slice(2, 4), value.slice(4)] : value.split(/[^\d]+/);
  const [day, month, rawYear] = parts;

  if (parts.length !== 3 || !day || !month || !rawYear || (rawYear.length !== 2 && rawYear.length !== 4)) return null;

  try {
    return Temporal.PlainDate.from(
      { day: Number(day), month: Number(month), year: Number(rawYear.length === 2 ? `20${rawYear}` : rawYear) },
      { overflow: 'reject' },
    );
  } catch {
    return null;
  }
};

export const formatTypedDate = (iso: string): string => {
  if (!isIsoDate(iso)) return iso;

  const [year, month, day] = iso.split('-');
  return `${day}.${month}.${year}`;
};

/** Следующий выбор в календаре: первый клик начинает период, второй заканчивает, порядок не важен. */
export const pickRangeDay = (range: DateRange, day: string): DateRange => {
  const start = isIsoDate(range.dateFrom) ? range.dateFrom : '';
  const end = isIsoDate(range.dateTo) ? range.dateTo : '';

  if (start && !end) return day < start ? { dateFrom: day, dateTo: start } : { dateFrom: start, dateTo: day };
  if (!start && end && day <= end) return { dateFrom: day, dateTo: end };

  return { dateFrom: day, dateTo: '' };
};

export type DateRangePreset = { value: string; label: string; range: DateRange };

export const dateRangePresets = (today: Temporal.PlainDate): DateRangePreset[] => {
  const yesterday = today.subtract({ days: 1 }).toString();
  const monthStart = today.with({ day: 1 });
  const previousMonth = monthStart.subtract({ months: 1 });
  // Месяц по имени: «Октябрь» понятнее, чем «Прошлый месяц».
  const monthName = (month: Temporal.PlainDate) => {
    const name = month.toLocaleString('ru-RU', { month: 'long' });
    return name.charAt(0).toUpperCase() + name.slice(1);
  };
  const untilToday = (dateFrom: Temporal.PlainDate) => ({ dateFrom: dateFrom.toString(), dateTo: today.toString() });

  return [
    { value: 'today', label: 'Сегодня', range: untilToday(today) },
    { value: 'yesterday', label: 'Вчера', range: { dateFrom: yesterday, dateTo: yesterday } },
    { value: 'week', label: '7 дней', range: untilToday(today.subtract({ days: 6 })) },
    { value: 'last-30-days', label: '30 дней', range: untilToday(today.subtract({ days: 29 })) },
    { value: 'month', label: monthName(today), range: untilToday(monthStart) },
    {
      value: 'previous-month',
      label: monthName(previousMonth),
      range: {
        dateFrom: previousMonth.toString(),
        dateTo: monthStart.subtract({ days: 1 }).toString(),
      },
    },
    { value: 'year', label: 'С начала года', range: untilToday(today.with({ month: 1, day: 1 })) },
  ];
};
